import React, { useState, useEffect } from 'react';
import withRoleAccess from '../../hoc/withRoleAccess';
import {
  Card,
  Row,
  Col,
  DatePicker,
  Button,
  Statistic,
  Spin,
  Alert,
  Typography,
  Space,
  message,
  Select,
  Input
} from 'antd';
import {
  DollarOutlined,
  FileTextOutlined,
  ReloadOutlined,
  CalendarOutlined,
  FilePdfOutlined,
  PieChartOutlined,
  RiseOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell, Area, AreaChart } from 'recharts';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

// Extend dayjs with isBetween plugin
dayjs.extend(isBetween);

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;

const API_URL = process.env.REACT_APP_API_URL;

const ExpenseReport = () => {
const [dateRange, setDateRange] = useState([
  dayjs().startOf('year'),
  dayjs().endOf('year')
]);

  
  const [expenseData, setExpenseData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchText, setSearchText] = useState('');

  // Real API calls using fetch
  const fetchExpenseData = async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/report/expense/report`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to fetch expense data');
    return await response.json();
  };

  const loadData = async () => {
    if (!API_URL) {
      setError('API URL is not configured. Please set REACT_APP_API_URL in your environment variables.');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication token not found. Please log in again.');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const data = await fetchExpenseData();
      setExpenseData(data);
      setFilteredData(data);
      message.success('Expense data loaded successfully');
    } catch (err) {
      console.error('Error loading expense data:', err);
      
      let errorMessage = 'Failed to load expense data';
      if (err.status) {
        if (err.status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (err.status === 400) {
          errorMessage = 'Invalid request parameters';
        } else {
          errorMessage = `Server error: ${err.status}`;
        }
      } else {
        errorMessage = err.message || 'An unexpected error occurred';
      }
      
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Filter data based on date range and other filters
  useEffect(() => {
    let filtered = expenseData.filter(expense => {
      const expenseDate = dayjs(expense.date);
      const isInDateRange = expenseDate.isBetween(dateRange[0], dateRange[1], 'day', '[]');
      const matchesCategory = categoryFilter === 'all' || expense.category_name === categoryFilter;
      const matchesSearch = searchText === '' || 
        expense.description.toLowerCase().includes(searchText.toLowerCase()) ||
        expense.category_name.toLowerCase().includes(searchText.toLowerCase()) ||
        expense.nickname.toLowerCase().includes(searchText.toLowerCase());
      
      return isInDateRange && matchesCategory && matchesSearch;
    });
    
    setFilteredData(filtered);
  }, [expenseData, dateRange, categoryFilter, searchText]);

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setDateRange(dates);
    }
  };

  const handleManualRefresh = () => {
    loadData();
  };

  const handlePrintPDF = () => {
    // Open a new window for printing
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    // Get the content to print
    const reportContainer = document.getElementById('expense-report-container');
    const printContent = reportContainer ? reportContainer.innerHTML : '';
    
    // Create the print document
    const printDocument = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Expense Analytics Report</title>
          <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/antd/5.0.0/reset.css">
          <style>
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              margin: 0;
              padding: 20px;
              background: white;
            }
            .ant-card { 
              box-shadow: none !important; 
              border: 1px solid #d9d9d9 !important;
              margin-bottom: 16px;
              page-break-inside: avoid;
            }
            .ant-statistic-title { font-weight: bold !important; }
            .ant-statistic-content { font-size: 18px !important; }
            .ant-card-head { 
              border-bottom: 2px solid #1890ff !important; 
              background: #f0f2f5 !important;
            }
            .ant-card-head-title { 
              font-weight: bold !important; 
              color: #1890ff !important; 
            }
            .print-header { 
              display: block !important;
              text-align: center; 
              margin-bottom: 20px; 
              border-bottom: 3px solid #1890ff; 
              padding-bottom: 10px; 
            }
            .print-date-range { 
              display: block !important;
              text-align: center; 
              margin-bottom: 15px; 
              font-weight: bold; 
            }
            svg { max-width: 100% !important; height: auto !important; }
            .no-print { display: none !important; }
            @page { 
              margin: 15mm; 
              size: A4; 
            }
          </style>
        </head>
        <body>
          ${printContent}
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `;
    
    // Write the document and trigger print
    printWindow.document.write(printDocument);
    printWindow.document.close();
  };

  // Calculate analytics
  const totalExpenses = filteredData.reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0);
  const expenseCount = filteredData.length;
  const averageExpense = expenseCount > 0 ? totalExpenses / expenseCount : 0;

  // Category breakdown
  const categoryBreakdown = filteredData.reduce((acc, expense) => {
    const category = expense.category_name || 'Unknown';
    if (!acc[category]) {
      acc[category] = { total: 0, count: 0 };
    }
    acc[category].total += parseFloat(expense.amount || 0);
    acc[category].count += 1;
    return acc;
  }, {});

  const categoryData = Object.entries(categoryBreakdown).map(([category, data]: [string, any]) => ({
    name: category,
    amount: data.total,
    count: data.count,
    percentage: totalExpenses > 0 ? ((data.total / totalExpenses) * 100).toFixed(1) : 0
  })).sort((a, b) => b.amount - a.amount);

  // Monthly trends
  const monthlyData = filteredData.reduce((acc, expense) => {
    const month = dayjs(expense.date).format('YYYY-MM');
    if (!acc[month]) {
      acc[month] = 0;
    }
    acc[month] += parseFloat(expense.amount || 0);
    return acc;
  }, {});

  const trendsData = Object.entries(monthlyData)
    .map(([month, amount]) => ({
      month: dayjs(month).format('MMM YYYY'),
      amount: amount
    }))
    .sort((a, b) => dayjs(a.month).valueOf() - dayjs(b.month).valueOf());

  // Expense type breakdown
  const typeBreakdown = filteredData.reduce((acc, expense) => {
    const type = expense.expense_type || 'Unknown';
    if (!acc[type]) {
      acc[type] = 0;
    }
    acc[type] += parseFloat(expense.amount || 0);
    return acc;
  }, {});

  const typeData = Object.entries(typeBreakdown).map(([type, amount]: [string, any]) => ({
    name: type,
    amount: amount
  })).sort((a, b) => b.amount - a.amount);

  // Get unique values for filters
  const uniqueCategories = [...new Set(expenseData.map(expense => expense.category_name))];
  const uniqueTypes = [...new Set(expenseData.map(expense => expense.expense_type))];

  // Colors for charts
  const COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#fa8c16', '#13c2c2', '#eb2f96'];


  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{`${label}`}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {`${entry.dataKey}: $${typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{`${payload[0].name}`}</p>
          <p style={{ color: payload[0].color }}>
            {`Amount: $${payload[0].value.toFixed(2)} (${payload[0].payload.percentage}%)`}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div id="expense-report-container" style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Print Header - Only visible when printing */}
        <div className="print-header" style={{ display: 'none' }}>
          <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
            Expense Analytics Report
          </Title>
          <Text>Generated on {new Date().toLocaleDateString()}</Text>
        </div>

        {/* Print Date Range - Only visible when printing */}
        <div className="print-date-range" style={{ display: 'none' }}>
          Report Period: {dateRange[0]?.format('YYYY-MM-DD')} to {dateRange[1]?.format('YYYY-MM-DD')}
        </div>

        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <Title level={2} style={{ marginBottom: '8px' }}>
            Expense Analytics Report
          </Title>
          <Text type="secondary">
            Comprehensive financial overview with category breakdown and spending trends
          </Text>
        </div>

        {/* Controls */}
        <Card style={{ marginBottom: '24px' }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <Space>
                <CalendarOutlined />
                <Text strong>Date Range:</Text>
              </Space>
              <RangePicker
                value={dateRange as any}
                onChange={handleDateRangeChange}
                format="YYYY-MM-DD"
                allowClear={false}
                style={{ width: '100%', marginTop: '8px' }}
              />
            </Col>
            <Col xs={24} sm={12} lg={4}>
              <Text strong>Category:</Text>
              <Select
                value={categoryFilter}
                onChange={setCategoryFilter}
                style={{ width: '100%', marginTop: '8px' }}
              >
                <Option value="all">All Categories</Option>
                {uniqueCategories.map(category => (
                  <Option key={category} value={category}>{category}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Text strong>Search:</Text>
              <Search
                placeholder="Search description, category, or user"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ marginTop: '8px' }}
              />
            </Col>
            <Col xs={24} sm={24} lg={4}>
              <div style={{ marginTop: '24px' }}>
                <Space>
                  <Button 
                    type="primary" 
                    icon={<ReloadOutlined />}
                    onClick={handleManualRefresh}
                    loading={loading}
                  >
                    Refresh
                  </Button>
                  <Button 
                    type="default" 
                    icon={<FilePdfOutlined />}
                    onClick={handlePrintPDF}
                    disabled={!expenseData.length}
                  >
                    Print PDF
                  </Button>
                </Space>
              </div>
            </Col>
          </Row>
        </Card>

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: '24px' }}
          />
        )}

        <Spin spinning={loading}>
          {/* Key Metrics */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Expenses"
                  value={totalExpenses}
                  precision={2}
                  prefix={<DollarOutlined />}
                  valueStyle={{ color: '#f5222d' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Number of Expenses"
                  value={expenseCount}
                  prefix={<FileTextOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Average Expense"
                  value={averageExpense}
                  precision={2}
                  prefix={<RiseOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Categories"
                  value={categoryData.length}
                  prefix={<PieChartOutlined />}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Charts Row */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            {/* Category Breakdown */}
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <PieChartOutlined style={{ color: '#1890ff' }} />
                    Expense by Category
                  </Space>
                }
              >
                {categoryData.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={categoryData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percentage }) => `${name} (${percentage}%)`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="amount"
                        >
                          {categoryData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <RechartsTooltip content={<PieTooltip active={undefined} payload={undefined} />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ marginTop: '16px' }}>
                      {categoryData.slice(0, 4).map((category, index) => (
                        <div key={category.name} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <Space>
                            <div 
                              style={{ 
                                width: '12px', 
                                height: '12px', 
                                backgroundColor: COLORS[index % COLORS.length],
                                borderRadius: '50%' 
                              }}
                            />
                            <Text>{category.name}</Text>
                          </Space>
                          <Text strong>${category.amount.toFixed(2)}</Text>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Text type="secondary">No expense data available</Text>
                  </div>
                )}
              </Card>
            </Col>

            {/* Monthly Trends */}
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <RiseOutlined style={{ color: '#52c41a' }} />
                    Monthly Spending Trends
                  </Space>
                }
              >
                {trendsData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={trendsData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <RechartsTooltip content={<CustomTooltip active={undefined} payload={undefined} label={undefined} />} />
                      <Area
                        type="monotone" 
                        dataKey="amount" 
                        stroke="#52c41a" 
                        fill="#52c41a" 
                        fillOpacity={0.3}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Text type="secondary">No trend data available</Text>
                  </div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Expense Type Chart */}
          {typeData.length > 0 && (
            <Card 
              title={
                <Space>
                  <BarChartOutlined style={{ color: '#722ed1' }} />
                  Expense by Type
                </Space>
              }
              style={{ marginBottom: '24px' }}
            >
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={typeData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <RechartsTooltip content={<CustomTooltip active={undefined} payload={undefined} label={undefined} />} />
                  <Bar dataKey="amount" fill="#722ed1" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}

          
        </Spin>

        <style {...{jsx: "true"} as any}>{`
          .print-header, .print-date-range {
            display: none;
          }
          
          @media print {
            .print-header, .print-date-range {
              display: block !important;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default withRoleAccess(ExpenseReport, ['superadmin']);